import type { DoctorShiftRule } from '../../domain/doctorShiftRule';
import type {
  DoctorShiftRuleRepository,
  DoctorShiftRuleFilter,
  DoctorShiftRuleListResult,
  CreateDoctorShiftRuleData,
  UpdateDoctorShiftRuleData
} from '../../application/doctorShiftRule/ports/DoctorShiftRuleRepository';
import { doctorShiftRuleApi } from '../api/doctorShiftRuleApi';

/**
 * DoctorShiftRuleRepository 実装（Clean Architecture: Infrastructure Layer）
 */
export class DoctorShiftRuleRepositoryImpl implements DoctorShiftRuleRepository {
  /**
   * シフトルール一覧取得（グループ化済み）
   * @param filter DoctorShiftRuleFilter 検索フィルター
   * @returns Promise<DoctorShiftRuleListResult> 一覧取得結果
   */
  async findAll(filter: DoctorShiftRuleFilter): Promise<DoctorShiftRuleListResult> {
    const params: Record<string, unknown> = {};

    if (filter.page !== undefined) {
      params.page = filter.page;
    }
    if (filter.pageSize !== undefined) {
      params.page_size = filter.pageSize;
    }
    if (filter.search !== undefined && filter.search !== '') {
      params.search = filter.search;
    }
    if (filter.doctorId !== undefined && filter.doctorId !== '') {
      params.doctor_id = filter.doctorId;
    }
    if (filter.ruleType !== undefined) {
      params.rule_type = filter.ruleType;
    }

    const response = await doctorShiftRuleApi.getAll(params as Parameters<typeof doctorShiftRuleApi.getAll>[0]);

    return {
      data: response.data,
      meta: {
        currentPage: response.meta.currentPage,
        lastPage: response.meta.lastPage,
        perPage: response.meta.perPage,
        total: response.meta.total
      }
    };
  }

  /**
   * シフトルール詳細取得
   * @param id string シフトルールID
   * @returns Promise<DoctorShiftRule | null> シフトルール詳細（見つからない場合null）
   */
  async findById(id: string): Promise<DoctorShiftRule | null> {
    try {
      const rule = await doctorShiftRuleApi.getById(id);
      return rule;
    } catch {
      // 404などの場合はnullを返す
      return null;
    }
  }

  /**
   * シフトルール作成（複数医師対応）
   * @param data CreateDoctorShiftRuleData 作成データ
   * @returns Promise<DoctorShiftRule[]> 作成されたシフトルール配列
   */
  async create(data: CreateDoctorShiftRuleData): Promise<DoctorShiftRule[]> {
    const rules = await doctorShiftRuleApi.create({
      doctorIds: data.doctorIds,
      ruleType: data.ruleType,
      timeConditions: data.timeConditions
    });
    return rules;
  }

  /**
   * シフトルール更新
   * @param id string シフトルールID
   * @param data UpdateDoctorShiftRuleData 更新データ
   * @returns Promise<DoctorShiftRule> 更新されたシフトルール
   */
  async update(id: string, data: UpdateDoctorShiftRuleData): Promise<DoctorShiftRule> {
    const rule = await doctorShiftRuleApi.update(id, {
      ruleType: data.ruleType,
      timeConditions: data.timeConditions
    });
    return rule;
  }

  /**
   * シフトルール削除（論理削除）
   * @param id string シフトルールID
   * @returns Promise<void> なし
   */
  async delete(id: string): Promise<void> {
    await doctorShiftRuleApi.delete(id);
  }
}

/**
 * Singleton インスタンス
 */
export const doctorShiftRuleRepository = new DoctorShiftRuleRepositoryImpl();
